import * as Sentry from '@sentry/react'

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN
const APP_ENV = import.meta.env.VITE_APP_ENV || 'development'

export function initSentry() {
  // Only initialize if DSN is provided
  if (!SENTRY_DSN) {
    if (APP_ENV !== 'development') {
      console.warn('Sentry DSN not configured. Error tracking disabled.')
    }
    return
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: APP_ENV,

    // Set sample rates based on environment
    tracesSampleRate: APP_ENV === 'production' ? 0.1 : 1.0,
    replaysSessionSampleRate: APP_ENV === 'production' ? 0.1 : 0,
    replaysOnErrorSampleRate: 1.0,

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Filter out common non-actionable errors
    beforeSend(event, hint) {
      const error = hint?.originalException

      // Ignore network errors that are often user/infra related
      if (error instanceof Error) {
        const message = error.message.toLowerCase()
        if (
          message.includes('network request failed') ||
          message.includes('failed to fetch') ||
          message.includes('load failed') ||
          message.includes('cancelled')
        ) {
          return null
        }
      }

      return event
    },

    // Don't send errors in development unless explicitly enabled
    enabled: APP_ENV !== 'development' || !!import.meta.env.VITE_SENTRY_DEBUG,
  })
}

// Utility to capture errors with context
export function captureError(error: Error, context?: Record<string, unknown>) {
  if (context) {
    Sentry.setContext('additional', context)
  }
  Sentry.captureException(error)
}

// Utility to set user context
export function setSentryUser(user: { id: string; email?: string; tenantId?: string }) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
  })
  if (user.tenantId) {
    Sentry.setTag('tenant_id', user.tenantId)
  }
}

// Clear user context on logout
export function clearSentryUser() {
  Sentry.setUser(null)
}

// Re-export Sentry for direct access when needed
export { Sentry }
